import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from 'state/axios';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useTheme, useMediaQuery, Box, Typography,  
    CircularProgress, TextField, Button, 
    MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { de, enGB } from 'date-fns/locale';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import TransportPoint from './TransportPoint';


const TransportPlacePage = () => {
    useAuthCheck();
    const { transportPlaceId } = useParams();    
    const navigate = useNavigate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    const locales = { 'en-us': undefined, 'en-gb': enGB, de };
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [airports, setAirports] = useState([]);
    const [transportPlace, setTransportPlace] = useState({});

    const initialValues = useMemo(() => ({
        placeName: transportPlace.placeName || '',
        address: transportPlace.address || '',
        airportIataCode: transportPlace.airportIataCode || '',
        notes: transportPlace.notes || '',
        pickupPoint: transportPlace.pickupPoint || {},
        dropoffPoint: transportPlace.dropoffPoint || {},
    }), [transportPlace]);
    

    const fetchAirports = useCallback(async () => {
        return await api.get('/airports');
    }, []);

    const fetchTransportPlaces = useCallback(async () => {
        if (transportPlaceId) {
            const url = `/transportplaces/${transportPlaceId}`;
            return await api.get(url);            
        }
    }, [transportPlaceId]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setError(null);
                setLoading(true);
                
                const requests = [fetchAirports()];
                if (transportPlaceId) {
                    requests.push(fetchTransportPlaces());
                }

                const [
                    airportsResponse, 
                    transportPlacesResponse,
                ] = await Promise.all(requests);

                setAirports(airportsResponse.data || []);
                if (transportPlacesResponse) {
                    setTransportPlace(transportPlacesResponse.data || {});
                }
               
            } catch (error) {
                setError(error);
                toast.error(error.response?.data?.message || "Failed to fetch data!");
            } finally {
                setLoading(false);
            }
        };
    
        fetchData();      
    }, [fetchAirports, 
        fetchTransportPlaces, 
        transportPlaceId,
    ]); 
    
    const checkoutSchema = yup.object().shape({
        placeName: yup.string().required('Place name is required'),
        airportIataCode: yup
            .string()
            .length(3, 'IATA code must be 3 characters')
            .required('Airport IATA Code is required'),
        pickupPoint: yup.object({
            name: yup.string().required('Pickup point is required.'),
        }),
        dropoffPoint: yup.object({
            name: yup.string().required('Dropoff point is required.'),
        }),
    });


    const handleFormSubmit = async (values) => {
        try {
            const method = transportPlaceId ? 'put' : 'post';
            const url = transportPlaceId
                ? `/transportplaces/update/${transportPlaceId}`
                : '/transportplaces/new';

            const data = {
                placeName: values.placeName,
                address: values.address,
                airportIataCode: values.airportIataCode.toUpperCase(),
                notes: values.notes,
                pickupPoint: values.pickupPoint,
                dropoffPoint: values.dropoffPoint,
            };

            await api({
                url,
                method,
                data,
            });

            toast.success(
                transportPlaceId
                    ? 'Transport place updated successfully.'
                    : 'Transport place created successfully.'
            );

            navigate('/transportplaces');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to submit the form!');
        }
    };


    return (
        <LocalizationProvider 
            dateAdapter={AdapterDateFns}
            adapterLocale={locales['en-gb']}
        >
            <Box className="main-container-box">                
                <FlexBetween>
                    <Header
                        title="Transport Place"
                        subtitle={`${loading ? 'Loading...' : transportPlaceId ? 'Update' : 'Create'}`}
                    />
                    {!loading && (
                        <Box display="flex" alignItems="center" gap={1}>
                            <StyledButton 
                                onClick={() => navigate("/transportplaces")}
                            >
                                <ArrowBackIosNewIcon />
                            </StyledButton>
                        </Box>
                    )}
                </FlexBetween>

                {loading ? (
                    <FlexCenter height={LAYOUT_HEIGHT}>
                        <CircularProgress />
                    </FlexCenter>
                ) : error ? (
                    <FlexCenter height={LAYOUT_HEIGHT}>
                        <Typography color="error">Error: {error?.message || ''}</Typography>                   
                    </FlexCenter>
                ) : (
                    <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                        <Formik
                            initialValues={initialValues}
                            enableReinitialize={true}
                            validationSchema={checkoutSchema}
                            onSubmit={handleFormSubmit}
                        >
                            {({
                                values,
                                errors,
                                touched,
                                handleBlur,
                                handleChange,
                                handleSubmit,
                                setFieldValue,
                            }) => (
                                <form onSubmit={handleSubmit}>
                                    <Box
                                        display="grid"
                                        gap={2}
                                        gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                        sx={{
                                            '& > div': { gridColumn: isDesktop ? undefined : 'span 12' },
                                        }}
                                    >                                            
                                        <TextField
                                            fullWidth
                                            required
                                            variant="filled"
                                            label="Place Name"
                                            name="placeName"
                                            value={values.placeName}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.placeName && !!errors.placeName}
                                            helperText={touched.placeName && errors.placeName}
                                            sx={{ gridColumn: 'span 6' }}
                                        />

                                        <TextField
                                            fullWidth
                                            variant="filled"
                                            label="Address"
                                            name="address"
                                            value={values.address}
                                            onChange={handleChange}
                                            sx={{ gridColumn: 'span 6' }}
                                        />
                                        <FormControl 
                                            required 
                                            variant="filled"
                                            sx={{ gridColumn: "span 6" }}
                                        >
                                            <InputLabel id="airport-label">
                                                Airport IATA Code
                                            </InputLabel>
                                            <Select
                                                label="Airport IATA Code"
                                                name="airportIataCode"
                                                variant="filled"
                                                value={values.airportIataCode}
                                                onChange={(e) =>
                                                    setFieldValue('airportIataCode', e.target.value)
                                                }
                                            >
                                                {airports?.map(ap => (
                                                    <MenuItem key={ap.iataCode} value={ap.iataCode}>
                                                        {ap.iataCode + ' - ' + ap.airportName}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        
                                        <TextField
                                            fullWidth
                                            variant="filled"
                                            label="Notes"
                                            name="notes"
                                            value={values.notes}
                                            onChange={handleChange}
                                            sx={{ gridColumn: 'span 6' }}
                                        />

                                        <Box gridColumn="span 12">
                                            <TransportPoint
                                                label="Pickup Point"
                                                value={values.pickupPoint}
                                                onChange={(v) => setFieldValue('pickupPoint', v)}
                                            />
                                        </Box>

                                        <Box gridColumn="span 12">
                                            <TransportPoint
                                                label="Dropoff Point"
                                                value={values.dropoffPoint}
                                                onChange={(v) => setFieldValue('dropoffPoint', v)}
                                            />
                                        </Box>

                                        <Button
                                            fullWidth
                                            type="submit"
                                            variant="contained"
                                            color="secondary"
                                            sx={{ gridColumn: "span 12", height: 42 }}
                                        >
                                            {transportPlaceId ? "Update" : "Create"}
                                        </Button>
                                    </Box>
                                </form>
                            )}
                        </Formik>
                    </Box>
                )}
            </Box>
        </LocalizationProvider>
    );
};

export default TransportPlacePage;
